import React, {useContext} from "react";
import {Box, Card, Stack, Typography} from "@mui/material";
import {
  BASE_WORLD_REGISTER_STEPS,
  BaseWorldRegisterPayload,
  CONTEXT_ACTION_TYPE,
  DataStoreContext,
  initialState
} from "../../contexts/OperatingEnvironmentContext";
import useMutation from "../../../../hooks/useMutation";
import Pjdb1ImputEx from "../../../../components/inputs/Pjdb1ImputEx";
import WithLabel from "../forms/WithLabel";
import Pjdb1FileSelect from "../../../../components/inputs/Pjdb1FileSelect";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import {ObjectSelectionField} from "../forms/ObjectSelectionField";
import {useObject} from "../../hooks/useObject";
import {fileUploade, registerBaseWorld} from "../../../../utils/awsAmplifyUtil";
import {buildGazeboUiUri} from "../../../../utils/gazeboUtils";
import {convertUnixTimeSec, makeDateJST} from "../../../../utils/dateUtil";

export default function BaseWorldRegisterModelSelectionStep() {
  const {state, dispatch} = useContext(DataStoreContext);
  const {fetchAll} = useObject({
    searchCond: state.baseWorldRegister.objectSearchCond,
    onChange: (newData, newStatus) => {
      dispatch({
        type: CONTEXT_ACTION_TYPE.BASE_WORLD_REGISTER,
        payload: {
          objects: newData,
          objectSearchStatus: newStatus
        } satisfies BaseWorldRegisterPayload
      });
    },
  });
  const [fileErr, setFileErr] = React.useState({err: false, errMsg: ""});
  const {mutate, formRef, isLoading, clear} = useMutation({
    enableConfirm: false,
    successMsg: 'ベースワールドの作成処理を開始しました',
    mutationFn: async () => {
      console.log("mutationFn");
      // 1. Upload files
      const uploadFileRes = await fileUploade(
        state.baseWorldRegister.inputs.baseWorldFiles.find(() => true) ?? null,
        {
          disablePrefix: true,
          basePath: `world/baseworld/${convertUnixTimeSec(makeDateJST())}`
        }
      );
      if (uploadFileRes.data === undefined) {
        return 'ファイルのアップロードに失敗しました。';
      }

      // 2. Register empty base world
      const registerRes = await registerBaseWorld({
        pathParams: {version: 'object-user'},
        body: {
          "s3-uri": uploadFileRes.data,
          objectIds: state.baseWorldRegister.inputs.selectedObjects.map(item => item.attr["object-id"]),
        }
      })
      if (typeof registerRes.data !== 'string') {
        return 'ベースワールドの登録に失敗しました。';
      }

      // 3. Open GazeboUI
      window.open(buildGazeboUiUri(uploadFileRes.data), '_blank');

      // 4. Move to next step
      dispatch({
        type: CONTEXT_ACTION_TYPE.BASE_WORLD_REGISTER,
        payload: {
          step: BASE_WORLD_REGISTER_STEPS.WORLD_NAME_INPUT_STEP,
          s3Uri: registerRes.data,
        } satisfies BaseWorldRegisterPayload
      });

      return true;
    },
    validate: () => {
      console.log("validate");
      if (state.baseWorldRegister.inputs.baseWorldFiles.length === 0) {
        setFileErr({err: true, errMsg: "ファイルを選択してください。"});
        return false;
      }
      return true;
    },
    onClear: () => {
      console.log("onClear");
      dispatch({
        type: CONTEXT_ACTION_TYPE.BASE_WORLD_REGISTER,
        payload: initialState.baseWorldRegister satisfies BaseWorldRegisterPayload
      });
      setFileErr({err: false, errMsg: ""});
    }
  });

  return (
    <Card sx={{p: 3, width: '100%'}}>
      <Stack gap={4}>
        <Box display='flex' flexWrap='wrap' gap={2} alignItems='flex-end' justifyContent='space-between'>
          <Typography variant="h6">ベースワールド</Typography>
          <Pjdb1ImputEx/>
        </Box>

        <Box>
          <Typography
            variant="body2">空ベースワールドと環境オブジェクトを選択後、ベースワールド作成開始を押してください。</Typography>
          <Typography variant="body2">ベースワールド作成には別タブで開くGazeboUIを利用します</Typography>
        </Box>

        <form ref={formRef}>
          <Stack gap={2}>
            <WithLabel label='ベースワールド'>
              <Pjdb1FileSelect
                multiple={false}
                label='ファイル選択'
                name='file'
                value={state.baseWorldRegister.inputs.baseWorldFiles}
                onChange={(_, files) => {
                  dispatch({
                    type: CONTEXT_ACTION_TYPE.BASE_WORLD_REGISTER,
                    payload: {
                      inputs: {
                        ...state.baseWorldRegister.inputs,
                        baseWorldFiles: files,
                      }
                    } satisfies BaseWorldRegisterPayload
                  });
                }}
                accept={{
                  'application/zip': ['.zip'],
                }}
                err={fileErr.err}
                errMsg={fileErr.errMsg}
                xs={8}
                toolTip='stl + urdfパッケージの空ベースワールドのzipファイルをアップロードする。'
              />
            </WithLabel>
            <WithLabel label='環境オブジェクト'>
              <Box sx={{width: '100%', mt: 2}}/>
              <ObjectSelectionField
                objects={state.baseWorldRegister.objects.map(item => Object.values(item.object)).flat()}
                fetchObjects={fetchAll}
                searchStatus={state.baseWorldRegister.objectSearchStatus}
                searchCond={state.baseWorldRegister.objectSearchCond}
                selectedObjects={state.baseWorldRegister.inputs.selectedObjects}
                onChange={(objects) => {
                  dispatch({
                    type: CONTEXT_ACTION_TYPE.BASE_WORLD_REGISTER,
                    payload: {
                      inputs: {
                        ...state.baseWorldRegister.inputs,
                        selectedObjects: objects
                      }
                    } satisfies BaseWorldRegisterPayload
                  });
                }}
                onChangeSearchCond={(searchCond) => {
                  dispatch({
                    type: CONTEXT_ACTION_TYPE.BASE_WORLD_REGISTER,
                    payload: {
                      objectSearchCond: searchCond
                    } satisfies BaseWorldRegisterPayload
                  });
                }}
              />
            </WithLabel>
          </Stack>
        </form>

        <Box component="section" sx={{textAlign: "center"}}>
          <Pjdb1Button
            label="クリア"
            variant="contained"
            color="inherit"
            isLoading={isLoading}
            sx={{m: 2}}
            onClick={clear}
          />
          {state.baseWorldRegister.s3Uri && (
            <Pjdb1Button
              label='GazeboUIを開く'
              variant="contained"
              color="info"
              isLoading={isLoading}
              sx={{m: 2}}
              onClick={() => {
                window.open(buildGazeboUiUri(state.baseWorldRegister.s3Uri!), '_blank');
              }}
            />
          )}
          {state.baseWorldRegister.s3Uri && (
            <Pjdb1Button
              label='次へ'
              variant="contained"
              color="primary"
              isLoading={isLoading}
              sx={{m: 2}}
              onClick={() => {
                dispatch({
                  type: CONTEXT_ACTION_TYPE.BASE_WORLD_REGISTER,
                  payload: {
                    step: BASE_WORLD_REGISTER_STEPS.WORLD_NAME_INPUT_STEP,
                  } satisfies BaseWorldRegisterPayload
                });
              }}
            />
          )}
          {state.baseWorldRegister.s3Uri === null && (
            <Pjdb1Button
              label="ベースワールド作成開始"
              variant="contained"
              color="primary"
              isLoading={isLoading}
              sx={{m: 2}}
              onClick={() => {
                mutate().catch(console.error)
              }}
            />
          )}
        </Box>
      </Stack>
    </Card>
  );
}
